import React from 'react';
import Router from './routes';
import { Toaster } from 'react-hot-toast';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { AuthProvider } from './features/auth/context/AuthContext';
import { ChatWebSocketProvider } from './contexts/ChatWebSocketContext';
import { config, isDevelopment, shouldEnablePerformanceMonitoring } from './config';
import { performanceMonitoring } from 'utils/performance';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import ChatSupport from './components/ChatSupport';
import GlobalDeepLinkHandler from './components/GlobalDeepLinkHandler';
import CallbackHandler from './components/CallbackHandler';

// Create query client with performance-optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
    mutations: {
      retry: false, // Disable automatic retries for mutations to prevent double API calls
    },
  },
});

function App(): React.JSX.Element {
  // Initialize chunk error handler and debugger
  React.useEffect(() => {
    // Chunk error handler initialized
    if (isDevelopment) {
      console.log('Dev tools available: window.__CHUNK_DEBUGGER__');
    }
  }, []);

  // Performance monitoring
  React.useEffect(() => {
    if (shouldEnablePerformanceMonitoring) {
      performanceMonitoring.measureExecution(() => {
        // App initialized with performance monitoring
      }, 'App Initialization');
    }
  }, []);

  console.log('process.env.REACT_APP_ENV', process.env.REACT_APP_ENV);

  return (
    <GlobalErrorBoundary>
      <div>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <AuthProvider>
            <ChatWebSocketProvider>
              <Toaster
                position="top-center"
                gutter={12}
                containerStyle={{ margin: "8px" }}
                toastOptions={{
                  success: {
                    duration: 3000,
                  },
                  error: {
                    duration: 5000,
                  },
                  style: {
                    fontSize: "16px",
                    maxWidth: "500px",
                    padding: "16px 24px",
                    backgroundColor: "white",
                    color: "black",
                  },
                }}
              />
              <GlobalDeepLinkHandler />
              <CallbackHandler />
              <Router />
              <ChatSupport />
            </ChatWebSocketProvider>
          </AuthProvider>
        </QueryClientProvider>
      </div>
    </GlobalErrorBoundary>
  );
}

export default App; 